import { lifecycleSteps, lifecycleStepIndex, date, number, percent } from "./purchase-order-presentation";
import {
  Check,
  History,
  Paperclip,
  Clock3,
  Gauge,
  AlertTriangle,
  Send,
  PackageCheck,
  X,
} from "lucide-react";
import type { PurchaseOrdersViewState } from "./purchase-order-view-state";
import type { PurchaseOrder } from "../domain/models";

type Props = Pick<PurchaseOrdersViewState,
  | "isPlanning"
  | "isQuality"
  | "setQualityStatus"
  | "setQualityNote"
  | "setQualityDisposition"
  | "setOperationError"
  | "setDialog"
  | "documentUrl"
  | "isSpecialist"
  | "submitting"
  | "setDispatchRecipient"
  | "setDeliveryAddress"
  | "openReceipt"
  | "setNote"> & { order: PurchaseOrder; remaining: number; isDraft: boolean; hasMeaningfulEvents: boolean; };

export function PurchaseOrderDetails({
  isPlanning,
  isQuality,
  setQualityStatus,
  setQualityNote,
  setQualityDisposition,
  setOperationError,
  setDialog,
  documentUrl,
  isSpecialist,
  submitting,
  setDispatchRecipient,
  setDeliveryAddress,
  openReceipt,
  setNote,
  order,
  remaining,
  isDraft,
  hasMeaningfulEvents,
}: Props) {
  return (<>
    <div className="purchase-order-detail is-operational">
      <ol
        className="purchase-order-lifecycle"
        aria-label="Sipariş süreci"
      >
        {lifecycleSteps.map((step, index) => (
          <li
            key={step.id}
            className={
              index < lifecycleStepIndex(order)
                ? "is-complete"
                : index === lifecycleStepIndex(order)
                  ? "is-current"
                  : ""
            }
          >
            <span>
              {index < lifecycleStepIndex(order) ? (
                <Check size={13} />
              ) : (
                index + 1
              )}
            </span>
            <strong>{step.label}</strong>
          </li>
        ))}
      </ol>
      <section
        className={`purchase-order-facts ${isPlanning ? "is-planning" : ""}`}
      >
        {isPlanning ? (
          <>
            <div>
              <span>Tedarikçi</span>
              <strong>{order.supplierName}</strong>
            </div>
            <div>
              <span>Sorumlu uzman</span>
              <strong>{order.assignedSpecialistName}</strong>
            </div>
            <div>
              <span>Planlanan teslimat</span>
              <strong>{date(order.expectedDeliveryAt)}</strong>
            </div>
            <div>
              <span>Kalan miktar</span>
              <strong>
                {number(remaining)} {order.unit}
              </strong>
            </div>
          </>
        ) : (
          <>
            <div>
              <span>Seçilen teklif</span>
              <strong>{order.selectedOfferNo}</strong>
            </div>
            <div>
              <span>Karar kaynağı</span>
              <strong>
                {order.approvalSource === "CONTRACT_PREAUTHORIZED"
                  ? `Sözleşme · ${order.contractNo ?? "Kayıt"}`
                  : "Nihai yönetim onayı"}
              </strong>
              <small>
                Teklif sürümü {order.offerVersion}
                {order.contractVersion
                  ? ` · Sözleşme sürümü ${order.contractVersion}`
                  : ""}
              </small>
            </div>
            <div>
              <span>Ödeme vadesi</span>
              <strong>{order.paymentDays} gün</strong>
            </div>
            <div>
              <span>Sorumlu uzman</span>
              <strong>{order.assignedSpecialistName}</strong>
            </div>
          </>
        )}
        {!isPlanning && order.sourceSystem !== "PLATFORM" ? (
          <div>
            <span>Kaynak</span>
            <strong>Mikro ERP</strong>
          </div>
        ) : null}
      </section>
      {order.status !== "CREATED" ? (
        <section
          className={`purchase-order-confirmation status-${(order.confirmationStatus ?? "AWAITING").toLowerCase()}`}
        >
          <div>
            <span>TEDARİKÇİ TEYİDİ</span>
            <strong>
              {order.confirmationStatus === "ACCEPTED"
                ? "Sipariş kabul edildi"
                : order.confirmationStatus === "REJECTED"
                  ? "Sipariş reddedildi"
                  : order.confirmationStatus === "CHANGE_REQUESTED"
                    ? "Değişiklik istendi"
                    : "Tedarikçi yanıtı bekleniyor"}
            </strong>
            <small>
              {order.confirmationUpdatedAt
                ? new Date(
                  order.confirmationUpdatedAt,
                ).toLocaleString("tr-TR")
                : "Güvenli bağlantı gönderildi"}
            </small>
          </div>
          {!isPlanning && order.supplierResponses?.[0] ? (
            <div>
              <span>Son yanıt</span>
              <strong>
                {order.supplierResponses[0].reason ||
                  `${order.supplierResponses[0].deliveryPlan.length} teslimat planı kaydedildi`}
              </strong>
            </div>
          ) : null}
        </section>
      ) : null}

      <div
        className={`purchase-order-detail-grid ${isPlanning ? "is-planning" : ""}`}
      >
        {!isDraft || order.deliveries.length > 0 ? (
          <section className="purchase-order-history">
            <header>
              <div>
                <History size={17} />
                <strong>Teslimat hareketleri</strong>
              </div>
              <span>{order.deliveries.length} kayıt</span>
            </header>
            {order.deliveries.length ? (
              order.deliveries.map((delivery) => (
                <article key={delivery.id}>
                  <div>
                    <small>{delivery.deliveryNo}</small>
                    <strong>{date(delivery.deliveredAt)}</strong>
                    <span>{delivery.recordedBy}</span>
                  </div>
                  <div>
                    <small>Teslim / plan</small>
                    <strong>
                      {number(delivery.quantity)} /{" "}
                      {number(delivery.expectedQuantity)}{" "}
                      {order.unit}
                    </strong>
                    <span>
                      {delivery.shortageQuantity > 0
                        ? `${number(delivery.shortageQuantity)} eksik`
                        : "Miktar tam"}
                    </span>
                  </div>
                  <div>
                    <small>Kalite</small>
                    <strong>
                      {delivery.qualityStatus === "ACCEPTED"
                        ? "Kabul edildi"
                        : delivery.qualityStatus === "CONDITIONAL"
                          ? "Koşullu kabul"
                          : delivery.qualityStatus === "REJECTED"
                            ? "Uygun değil"
                            : "Kontrol bekliyor"}
                    </strong>
                    <span>
                      {delivery.qualityNote ||
                        (delivery.qualityStatus === "PENDING"
                          ? "Kalite birimi değerlendirecek"
                          : "Gerekçe yok")}
                    </span>
                    {delivery.capaNo ? (
                      <span>DÖF: {delivery.capaNo}</span>
                    ) : null}
                    {isQuality &&
                      delivery.qualityStatus === "PENDING" ? (
                      <button
                        className="secondary-button"
                        onClick={() => {
                          setQualityStatus("ACCEPTED");
                          setQualityNote("");
                          setQualityDisposition("CREATE_CAPA");
                          setOperationError("");
                          setDialog({
                            kind: "quality",
                            order,
                            deliveryId: delivery.id,
                          });
                        }}
                      >
                        Kalite sonucunu kaydet
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <small>Gecikme / iade</small>
                    <strong>
                      {delivery.delayDays
                        ? `${delivery.delayDays} gün gecikme`
                        : "Zamanında"}
                    </strong>
                    <span>
                      {delivery.returnQuantity
                        ? `${number(delivery.returnQuantity)} ${order.unit} iade · ${delivery.returnDisposition === "DEDUCT_FROM_ORDER" ? "siparişten düşüldü" : "yenisi bekleniyor"}`
                        : "İade yok"}
                    </span>
                  </div>
                  <div className="purchase-order-delivery-documents">
                    <small>İrsaliye</small>
                    <strong>
                      {delivery.deliveryNoteNo ||
                        "Numara kaydedilmemiş"}
                    </strong>
                    {delivery.documents.map((document) => (
                      <a
                        key={document.id}
                        href={documentUrl(
                          order.id,
                          document.id,
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Paperclip size={14} />
                        {document.originalName}
                      </a>
                    ))}
                  </div>
                </article>
              ))
            ) : (
              <div className="purchase-order-history-empty">
                <Clock3 size={18} />
                <span>Henüz teslimat hareketi kaydedilmedi.</span>
              </div>
            )}
          </section>
        ) : null}

        {hasMeaningfulEvents ? (
          <section className="purchase-order-history purchase-order-events">
            <header>
              <div>
                <History size={17} />
                <strong>Sipariş hareketleri</strong>
              </div>
              <span>{order.events.length} kayıt</span>
            </header>
            {order.events.length ? (
              order.events.map((event) => (
                <article key={event.id}>
                  <div>
                    <small>
                      {event.eventType === "PURCHASE_ORDER_CREATED"
                        ? "Sipariş taslağı oluşturuldu"
                        : event.eventType === "PURCHASE_ORDER_SENT"
                          ? "Tedarikçiye iletildi"
                          : event.eventType ===
                            "PURCHASE_ORDER_QUALITY_ACCEPTED"
                            ? "Kalite kabul edildi"
                            : event.eventType ===
                              "PURCHASE_ORDER_QUALITY_REJECTED"
                              ? "Kalite uygun bulunmadı"
                              : event.eventType}
                    </small>
                    <strong>
                      {new Intl.DateTimeFormat("tr-TR", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(new Date(event.occurredAt))}
                    </strong>
                    <span>
                      {event.actorName} · {event.actorRole}
                    </span>
                    {!isPlanning && event.reason ? (
                      <span>{event.reason}</span>
                    ) : null}
                  </div>
                </article>
              ))
            ) : (
              <div className="purchase-order-history-empty">
                <Clock3 size={18} />
                <span>Henüz sipariş hareketi bulunmuyor.</span>
              </div>
            )}
          </section>
        ) : null}

        {!isPlanning ? (
          <aside
            className={`purchase-order-performance risk-${order.supplierPerformance.risk.toLowerCase()}`}
          >
            <header>
              <div>
                <Gauge size={18} />
                <span>Veriye dayalı değerlendirme</span>
              </div>
              <strong>
                {order.supplierPerformance.score}
                <small>/100</small>
              </strong>
            </header>
            <p>
              {order.supplierPerformance.risk === "LOW"
                ? "Düşük operasyon riski"
                : order.supplierPerformance.risk === "MEDIUM"
                  ? "Yakın takip önerilir"
                  : "Yüksek operasyon riski"}
            </p>
            <div className="purchase-order-performance-glance">
              <span>
                <small>Zamanında</small>
                <strong>
                  {percent(order.supplierPerformance.onTimeRate)}
                </strong>
              </span>
              <span>
                <small>Kalite</small>
                <strong>
                  {percent(
                    order.supplierPerformance.qualityAcceptanceRate,
                  )}
                </strong>
              </span>
              <span>
                <small>Geçmiş</small>
                <strong>
                  {order.supplierPerformance.orderCount} sipariş
                </strong>
              </span>
            </div>
            <details>
              <summary>Tüm performans ölçümlerini göster</summary>
              <dl>
                <div>
                  <dt>Eksik teslimat</dt>
                  <dd>
                    {percent(order.supplierPerformance.shortageRate)}
                  </dd>
                </div>
                <div>
                  <dt>İade oranı</dt>
                  <dd>
                    {percent(order.supplierPerformance.returnRate)}
                  </dd>
                </div>
                <div>
                  <dt>Fiyat tutarlılığı</dt>
                  <dd>
                    {percent(
                      order.supplierPerformance.priceConsistencyRate,
                    )}
                  </dd>
                </div>
              </dl>
              <ul>
                {order.supplierPerformance.rationale.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <small>
                Bu puan karar desteğidir; nihai değerlendirme yetkili
                kullanıcıya aittir.
              </small>
            </details>
          </aside>
        ) : null}
      </div>

      {order.status === "CANCELLED" && order.cancellationReason ? (
        <div className="purchase-order-cancellation">
          <AlertTriangle size={16} />
          <div>
            <strong>İptal gerekçesi</strong>
            <span>{order.cancellationReason}</span>
          </div>
        </div>
      ) : null}
      {order.dispatchStatus === "FAILED" ? (
        <div className="purchase-order-inline-error" role="alert">
          <AlertTriangle size={16} />
          <div>
            <strong>Tedarikçiye gönderim başarısız</strong>
            <span>
              {order.dispatchError || "Gönderim sonucu alınamadı."}
            </span>
          </div>
        </div>
      ) : null}
      {isSpecialist &&
        !["RECEIVED", "CANCELLED"].includes(order.status) ? (
        <div className="purchase-order-actions">
          {order.status === "CREATED" ? (
            <button
              className="approve-button"
              disabled={
                submitting || order.dispatchStatus === "PENDING"
              }
              onClick={() => {
                setDispatchRecipient(
                  order.dispatchRecipient ??
                  order.suggestedDispatchRecipient ??
                  "",
                );
                setDeliveryAddress(order.deliveryAddress ?? "");
                setOperationError("");
                setDialog({ kind: "send", order });
              }}
            >
              <Send size={15} />{" "}
              {order.dispatchStatus === "FAILED"
                ? "İletimi yeniden dene"
                : order.dispatchStatus === "PENDING"
                  ? "İletim bekleniyor"
                  : "Tedarikçiye ilet"}
            </button>
          ) : null}
          {["SENT", "PARTIALLY_RECEIVED"].includes(order.status) ? (
            <button
              className="approve-button"
              onClick={() => openReceipt(order)}
            >
              <PackageCheck size={15} /> Teslimat kaydet
            </button>
          ) : null}
          <button
            type="button"
            className="danger-button is-secondary"
            onClick={() => {
              setNote("");
              setDialog({ kind: "cancel", order });
            }}
          >
            <X size={15} aria-hidden="true" /> Siparişi iptal et
          </button>
        </div>
      ) : null}
    </div>
  </>);
}
